import { Form, Input, Switch } from "antd";
import { useEffect } from "react";
import type { UpdateEnvironmentInput } from "@/common/artemis-sdk/graphql";
import type {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentProvider,
} from "@/common/model";
import { AutomationPolicyField } from "@/components/environments/automation-policy-ladder";
import {
	CLASSIFICATION_OPTIONS,
	PROVIDER_METADATA_FIELDS,
	PROVIDER_METADATA_GROUPS,
} from "@/components/environments/environment-base";
import {
	collectMetadata,
	MetadataFieldItem,
} from "@/components/environments/environment-metadata-fields";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";

/** Existing environment values used to seed the edit wizard. */
export interface EnvironmentInitialValues {
	name: string;
	provider: EnvironmentProvider;
	classification: EnvironmentClassification;
	autoApproveUpTo: AutoApprovalThreshold;
	/** Existing metadata key-value pairs from the environment record. */
	metadata: Array<{ key: string; value: string }>;
	/** Persisted user-controlled flags (source of truth for the group toggles). */
	allowAgentsToBeDeployed: boolean;
	allowAgentsToAccess: boolean;
}

interface EnvironmentCreateWizardProps {
	open: boolean;
	onClose: () => void;
	/** Environment being edited — fields are pre-filled and the complete button reads "Save". */
	initialValues: EnvironmentInitialValues;
	/** Called with the fields to update. */
	onUpdate: (input: UpdateEnvironmentInput) => void;
}

/**
 * Drawer wizard for **editing** an environment (name, classification, automation policy, metadata,
 * and the capability toggles). First-time creation uses the dedicated
 * `EnvironmentOnboardingWizard`; this component only ever runs in edit mode.
 */
export function EnvironmentCreateWizard({
	open,
	onClose,
	initialValues,
	onUpdate,
}: EnvironmentCreateWizardProps) {
	const [form] = Form.useForm();
	const provider = initialValues.provider;
	const metadataFields = PROVIDER_METADATA_FIELDS[provider];
	const formValues = Form.useWatch((vals) => vals, form) ?? {};

	// Seed form fields whenever the wizard opens.
	useEffect(() => {
		if (!open) return;
		const groups = PROVIDER_METADATA_GROUPS[provider] ?? [];
		const fields = PROVIDER_METADATA_FIELDS[provider] ?? [];
		// Secret fields are write-only: never prefill them (the API only returns a mask).
		const secretKeys = new Set(
			fields.filter((field) => field.secret).map((field) => field.key),
		);
		const metadataFormValues = Object.fromEntries(
			initialValues.metadata
				.filter(({ key }) => !secretKeys.has(key))
				.map(({ key, value }) => [`meta_${key}`, value]),
		);
		// The group toggles ARE the persisted flags (not inferred from ARN presence), so a user can
		// keep the ARNs but leave the flag off.
		const groupFlagByKey: Record<string, boolean> = {
			agentAccess: initialValues.allowAgentsToAccess,
			agentDeployment: initialValues.allowAgentsToBeDeployed,
		};
		const groupFormValues = Object.fromEntries(
			groups.map((group) => [
				`group_${group.key}`,
				groupFlagByKey[group.key] ?? false,
			]),
		);
		form.setFieldsValue({
			name: initialValues.name,
			classification: initialValues.classification,
			autoApproveUpTo: initialValues.autoApproveUpTo,
			...metadataFormValues,
			...groupFormValues,
		});
	}, [open, initialValues, provider, form]);

	// Secret keys already stored on the record — these can be explicitly cleared.
	const configuredSecretKeys = new Set(
		initialValues.metadata
			.filter(({ key }) =>
				(metadataFields ?? []).some(
					(field) => field.key === key && field.secret,
				),
			)
			.map(({ key }) => key),
	);

	// A group's fields are shown when its toggle is on OR it already has stored metadata — so
	// existing ARNs stay visible/editable even if the flag is currently off.
	const groupsWithExistingMetadata = new Set(
		initialValues.metadata
			.map((m) => (metadataFields ?? []).find((f) => f.key === m.key)?.group)
			.filter((g): g is string => Boolean(g)),
	);
	const isGroupOpen = (groupKey: string) =>
		Boolean(formValues[`group_${groupKey}`]) ||
		groupsWithExistingMetadata.has(groupKey);

	const handleComplete = () => {
		form.validateFields().then((vals) => {
			const metadata = collectMetadata(vals, { provider, isEditMode: true });
			onUpdate({
				name: vals.name as string,
				classification: vals.classification as EnvironmentClassification,
				autoApproveUpTo: vals.autoApproveUpTo as AutoApprovalThreshold,
				metadata,
				allowAgentsToAccess: Boolean(vals.group_agentAccess),
				allowAgentsToBeDeployed: Boolean(vals.group_agentDeployment),
			});
			onClose();
			form.resetFields();
		});
	};

	return (
		<Wizard
			open={open}
			title="Environment"
			completeLabel="Save"
			form={form}
			size="large"
			onComplete={handleComplete}
			onCancel={() => {
				onClose();
				form.resetFields();
			}}
			pages={[
				{
					id: "details",
					title: "Details",
					fields: ["name"],
					content: (
						<>
							<Form.Item
								name="name"
								label="Name"
								rules={[
									{ required: true },
									{ max: 255, message: "Name must be 255 characters or less" },
								]}
							>
								<Input placeholder="Environment name" maxLength={255} />
							</Form.Item>
							<Form.Item
								name="classification"
								label="Classification"
								rules={[{ required: true }]}
							>
								<SegmentedSelection options={CLASSIFICATION_OPTIONS} />
							</Form.Item>
						</>
					),
				},
				...(metadataFields
					? [
							{
								id: "metadata",
								title: "Config",
								// Validate only the fields currently visible (grouped fields appear when
								// their switch is on or they have stored metadata).
								fields: metadataFields
									.filter((f) => !f.group || isGroupOpen(f.group))
									.map((f) => `meta_${f.key}`),
								content: (
									<>
										{metadataFields
											.filter((field) => !field.group)
											.map((field) => (
												<MetadataFieldItem
													key={field.key}
													field={field}
													isEditMode
													configuredSecret={configuredSecretKeys.has(field.key)}
												/>
											))}
										{(PROVIDER_METADATA_GROUPS[provider] ?? []).map((group) => (
											<div key={group.key}>
												<Form.Item
													name={`group_${group.key}`}
													label={group.label}
													extra={group.description}
													valuePropName="checked"
													initialValue={false}
												>
													<Switch size="small" />
												</Form.Item>
												{isGroupOpen(group.key) &&
													metadataFields
														.filter((field) => field.group === group.key)
														.map((field) => (
															<MetadataFieldItem
																key={field.key}
																field={field}
																isEditMode
																configuredSecret={configuredSecretKeys.has(
																	field.key,
																)}
															/>
														))}
											</div>
										))}
									</>
								),
							},
						]
					: []),
				{
					id: "policy",
					title: "Automation policy",
					content: (
						<AutomationPolicyField
							initialValue={initialValues.autoApproveUpTo}
						/>
					),
				},
			]}
		/>
	);
}
